import React, { useState, useEffect } from 'react';
import './Messages.css';
import { getOrderMessages, sendOrderMessages, setOrderStatus } from '../../../API_Handler';

const MessagesComponent = ({ selectedOrder, handleCloseMessagePanel }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // States for message input and file upload
  const [newMessage, setNewMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [sending, setSending] = useState(false);

  // Function to fetch messages for the selected order
  const fetchMessages = async (orderId) => {
    setLoadingMessages(true);

    try {
      const fetchedMessages = await getOrderMessages(orderId);
      // Sort messages by creation time (oldest first)
      fetchedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Function to send a new message and update order status
  const sendMessage = async () => {
    if (!newMessage.trim() && !attachmentFile) {
      return; // Don't send empty messages without attachment
    }

    setSending(true);

    try {
      let attachmentUrl = null;
      if (attachmentFile) {
        // TODO: Implement actual S3 upload logic here
        attachmentUrl = null; // Replace with actual URL after upload
      }


      const createdMessage = await sendOrderMessages(selectedOrder.id, newMessage, attachmentUrl);

      // Prepend the new message to maintain reverse chronological order
      setMessages((prevMessages) => [...prevMessages, createdMessage]);

      // Update the order status to 'AWAITING USER ACTION'
      const updatedOrder = await setOrderStatus(selectedOrder.id, 'AWAITING USER ACTION');

      // Clear the input fields
      setNewMessage('');
      setAttachmentFile(null);
    } catch (error) {
      console.error('Error sending message or updating order status:', error);
      // Optionally, implement user-facing error handling here (e.g., notifications)
    } finally {
      //sending it with false because we don't want to the user that we are reloading stuff..
      setSending(false);
    }
  };


  // Fetch messages when the selected order changes
  useEffect(() => {
    if (selectedOrder) {
      fetchMessages(selectedOrder.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder]);


  // Function to determine status highlight color
  const getStatusClass = (status) => {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'AWAITING VENDOR ACTION':
        return 'status-awaiting-vendor';
      case 'AWAITING USER ACTION':
        return 'status-awaiting-user';
      case 'CLOSED':
        return 'status-closed';
      default:
        return '';
    }
  };

  return (
    <div className="messages-container flex flex-col h-full">
      <div className="messages-area flex flex-col h-full relative bg-white overflow-hidden">
        {selectedOrder ? (
          <>
            {/* Order info fixed at the top */}
            <div className="messages-header sticky top-0 bg-white p-4 z-10 border-b border-gray-300 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Order #{selectedOrder.id}</h2>
              <button className="modal-close-btn text-gray-500 hover:text-gray-700" onClick={handleCloseMessagePanel}>X</button>
            </div>

            {/* Messages are scrollable in the middle */}
            <div className="messages-content flex-grow overflow-y-auto p-4">
              {loadingMessages ? (
                <div className="loading text-center text-gray-600 text-lg">Loading messages...</div>
              ) : (
                <div className="messages-list flex flex-col gap-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`ticket-message p-3 rounded-lg ${/^\d+$/.test(msg.sender) ? 'border-l-4 border-charmPurple' : 'bg-green-100 border-l-4 border-green-500'}`}
                    >
                      <div className="message-header flex justify-between mb-1">
                        <span className="sender font-semibold text-gray-800">{/^\d+$/.test(msg.sender) ? 'You' : msg.sender}</span>
                        <span className="timestamp text-sm text-gray-500">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {msg.message.split('\n').map((line, i) => (
                          <React.Fragment key={i}>{line}<br /></React.Fragment>
                        ))}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message input fixed at the bottom */}
            <div className="message-input sticky bottom-0 bg-white p-4 border-t border-gray-300 z-10 flex flex-col gap-2">
              <textarea
                className="w-full resize-none p-2 border border-gray-300 rounded-md focus:border-pink-700 transition-colors"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows="1"
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={sendMessage}
                  disabled={sending}
                  className={`send-button bg-pink-700 text-white rounded-md py-2 px-4 text-base font-medium hover:bg-pink-800 transition-all ${sending ? 'cursor-not-allowed' : ''}`}
                >
                  {sending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="no-selection flex flex-1 items-center justify-center text-gray-500 text-xl">
            Select an order to view messages.
          </div>
        )}
      </div>
    </div>


  );
};

export default MessagesComponent;
